import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RadarChart from './RadarChart';
import GlassWarp from './GlassWarp';
// Removed UnicornStudio integration - not working
import { BECOMING_AXES, ITEMS_PER_DIM, calculateArchetype, DIMENSION_WHY } from './quizData';
import './QuizResults.css';
import './QuizResults-modern.css';

const QuizResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 820);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const scores = location.state?.scores || {};
  const archetype = calculateArchetype(scores);

  useEffect(() => {
    // Simulate processing time for better UX
    setTimeout(() => {
      setIsLoading(false);
      setTimeout(() => setShowResults(true), 100);
    }, 2000);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 820);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll-triggered animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, observerOptions);

    // Observe all sections and animated elements
    const sections = document.querySelectorAll('.results-section-minimal, .mobile-value-item, .pole-item, .suggestion-item, .legend-row, .results-cta');
    sections.forEach(section => observer.observe(section));

    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, [showResults]);

  useEffect(() => {
    // Mouse move parallax effect for hero section
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      
      const x = (clientX / innerWidth - 0.5) * 2;
      const y = (clientY / innerHeight - 0.5) * 2;
      
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Normalize scores to 0-1 range for radar chart
  const normalizedValues = BECOMING_AXES.map(dim => {
    const score = scores[dim] || 0;
    const items = ITEMS_PER_DIM[dim] || 6;
    const maxAbs = items * 2;
    // Map from [-maxAbs, +maxAbs] to [0, 1]
    return (score + maxAbs) / (2 * maxAbs);
  });

  // Get dominant poles
  const getDominantPoles = () => {
    return Object.entries(scores)
      .map(([dim, score]) => {
        const parts = dim.split('↔');
        const pole = score > 0 ? parts[1] : (score < 0 ? parts[0] : 'Balanced');
        return { dimension: dim, pole, score, absScore: Math.abs(score) };
      })
      .sort((a, b) => b.absScore - a.absScore)
      .slice(0, 3);
  };

  const dominantPoles = getDominantPoles();

  // Get growth suggestions
  const getGrowthSuggestions = () => {
    const suggestions = [];
    
    // Find lowest scoring dimensions (areas for growth)
    const sortedByAbs = Object.entries(scores)
      .map(([dim, score]) => ({ dim, score, absScore: Math.abs(score) }))
      .sort((a, b) => a.absScore - b.absScore)
      .slice(0, 2);

    sortedByAbs.forEach(({ dim, score }) => {
      const parts = dim.split('↔');
      if (Math.abs(score) < 3) {
        suggestions.push({
          dimension: dim,
          suggestion: `Explore both ${parts[0]} and ${parts[1]} to find your balance in this dimension.`,
          type: 'balanced'
        });
      } else {
        const toward = score > 0 ? parts[0] : parts[1];
        suggestions.push({
          dimension: dim,
          suggestion: `Consider exploring more ${toward} to round out your experience.`,
          type: 'expand'
        });
      }
    });

    return suggestions;
  };

  const growthSuggestions = getGrowthSuggestions();

  // Get specific practices for each dimension
  const getPracticeForDimension = (dimension, type) => {
    const practices = {
      "Freedom↔Stability": type === 'balanced' 
        ? "Experiment with one week of structure, one week of spontaneity. Notice which feels more natural and which offers unexpected benefits."
        : "If you lean toward one pole, try 'micro-doses' of the opposite: plan a spontaneous day trip, or create a simple weekly routine.",
      "Growth↔Contentment": type === 'balanced'
        ? "Practice gratitude journaling for what you have, then set one small stretch goal. Notice how both can coexist."
        : "High achievers: spend 10 minutes daily appreciating present success. Contentment-seekers: choose one area for deliberate improvement.",
      "Authenticity↔Harmony": type === 'balanced'
        ? "In low-stakes situations, practice both: be direct with a close friend, then practice diplomatic communication at work."
        : "Authentic types: practice reading the room before speaking. Harmony types: share one uncomfortable truth with someone you trust.",
      "Impact↔Inner peace": type === 'balanced'
        ? "Alternate between action-oriented weeks and contemplative weeks. Track your energy and fulfillment in both modes."
        : "Impact-driven: schedule weekly stillness (meditation, nature). Inner peace seekers: choose one small way to influence your community.",
      "Connection↔Independence": type === 'balanced'
        ? "Design your week with both deep connection time and solo reflection time. See how each fuels the other."
        : "Connection-oriented: practice comfortable solitude for 2-3 hours weekly. Independence-oriented: initiate vulnerable conversations.",
      "Expression↔Mastery": type === 'balanced'
        ? "Alternate between raw creative expression (no judgment) and deliberate skill-building sessions."
        : "Expressive types: dedicate time to refining one skill. Mastery types: create something messy and imperfect for pure expression.",
      "Simplicity↔Pleasure": type === 'balanced'
        ? "Spend one week minimizing, one week savoring. Notice what brings sustainable joy versus fleeting satisfaction."
        : "Simplicity types: indulge in one rich experience weekly. Pleasure types: practice essentialism—remove one unnecessary complexity."
    };
    return practices[dimension] || "Explore both poles through intentional practice and reflection.";
  };

  const handleRetake = () => {
    navigate('/quiz');
  };

  const handleDownloadApp = () => {
    window.open('https://apps.apple.com/us/app/geists-talk-to-anyone/id6737850836', '_blank');
  };

  if (isLoading) {
    return (
      <div className="results-loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h2 className="loading-text">Analyzing your responses...</h2>
          <p className="loading-subtext">Discovering your unique archetype</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`results-container ${showResults ? 'show' : ''}`}>
      {/* Glass Warp Effect */}
      {!isMobile && <GlassWarp mousePosition={mousePosition} />}
      
      {/* Scroll Progress Indicator */}
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} />
      
      <div className="results-content">
        {/* Hero Section */}
        <div className={`results-hero ${archetype.name === 'The Visionary' ? 'visionary-hero' : ''}`}>
          {/* Background gradient */}
          <div 
            className="results-hero-background"
            style={{ 
              position: 'absolute', 
              inset: 0, 
              zIndex: 0,
              background: archetype.name === 'The Visionary' 
                ? archetype.colorHex 
                : `linear-gradient(135deg, ${archetype.colorHex}20, ${archetype.colorHex}40)`
            }}
          />
          {/* Interactive Mouse Glow */}
          <div 
            className="hero-glow" 
            style={{
              left: `${(mousePosition.x + 1) * 50}%`,
              top: `${(mousePosition.y + 1) * 50}%`,
            }}
          />
          
          {/* Clean hero layout */}
          <h1 
            className={`results-title ${archetype.name === 'The Visionary' ? 'visionary-title' : ''}`}
            style={{
              transform: `translate(${mousePosition.x * 5}px, ${mousePosition.y * 5}px)`,
              color: archetype.name === 'The Visionary' ? '#fff' : undefined
            }}
          >
            You are
          </h1>
          <h2 
            className={`archetype-name ${archetype.name === 'The Visionary' ? 'visionary-name' : ''}`}
            style={{
              transform: `translate(${mousePosition.x * 10}px, ${mousePosition.y * 10}px)`,
              fontFamily: archetype.name === 'The Visionary' ? "'GT-America-Mono', monospace" : undefined
            }}
          >
            {archetype.name}
          </h2>
          <p 
            className={`archetype-mantra ${archetype.name === 'The Visionary' ? 'visionary-mantra' : ''}`}
            style={{
              transform: `translate(${mousePosition.x * 8}px, ${mousePosition.y * 8}px)`,
              fontFamily: archetype.name === 'The Visionary' ? "'GT-America-Mono', monospace" : undefined,
              color: archetype.name === 'The Visionary' ? '#fff' : undefined
            }}
          >
            {archetype.mantra}
          </p>
        </div>

        {/* Radar Chart Section or Mobile List */}
        <div className="results-section-minimal">
          <h3 className="section-title-minimal">YOUR VALUE MAP</h3>
          <p className="section-description-minimal">
            {isMobile 
              ? 'Your unique balance across seven core dimensions of being.'
              : 'This radar chart visualizes your unique balance across seven core dimensions of being. The dashed grey line shows a perfectly balanced profile (50% on all axes) for comparison.'}
          </p>
          {!isMobile ? (
            <div className="radar-chart-wrapper-minimal">
              <RadarChart 
                axes={BECOMING_AXES}
                values={normalizedValues}
                color={archetype.colorHex}
                size={1000}
                showRingLabels={true}
                showPoleLabels={true}
                showBalancedReference={true}
              />
            </div>
          ) : (
            <div className="mobile-values-list">
              {BECOMING_AXES.map((axis, i) => {
                const v = normalizedValues[i];
                const pct = Math.round(v * 100);
                const parts = axis.split('↔');
                const leftPole = (parts[0] || '').trim();
                const rightPole = (parts[1] || '').trim();
                
                let leaning = '';
                let leaningDirection = 'balanced';
                
                if (v > 0.55) {
                  leaning = rightPole;
                  leaningDirection = 'right';
                } else if (v < 0.45) {
                  leaning = leftPole;
                  leaningDirection = 'left';
                } else {
                  leaning = 'Balanced';
                  leaningDirection = 'balanced';
                }
                
                const biasText = leaningDirection === 'balanced' 
                  ? `You are balanced between ${leftPole} and ${rightPole}`
                  : `You bias toward ${leaning} by ${Math.abs(pct - 50)}%`;
                
                return (
                  <div key={axis} className="mobile-value-item">
                    <div className="mobile-value-header">
                      <span className="mobile-value-axis">{leftPole} vs. {rightPole}</span>
                    </div>
                    <div className="mobile-value-bar-wrapper">
                      <div className="mobile-value-bar">
                        <div 
                          className="mobile-value-fill" 
                          style={{ 
                            width: `${pct}%`, 
                            backgroundColor: archetype.colorHex 
                          }}
                        />
                      </div>
                    </div>
                    <div className="mobile-value-result" style={{ color: archetype.colorHex }}>
                      {biasText}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* What This Means - Scientific Context */}
        <div className="results-section-minimal">
          <h3 className="section-title-minimal">What This Means</h3>
          <div className="science-context">
            <p className="science-intro">
              Your profile maps your core <strong>motivational values</strong>—the psychological forces that guide your choices, relationships, and sense of meaning.
            </p>
            <div className="science-framework">
              <div className="framework-badge">Research-Based</div>
              <p>
                This assessment draws from <strong>Schwartz Theory of Basic Human Values</strong> (validated across 80+ countries), <strong>Self-Determination Theory</strong> (autonomy, competence, relatedness), and <strong>Moral Foundations Theory</strong> (psychological roots of ethics). These aren't personality traits—they're the deeper structure of what you find important.
              </p>
            </div>
            <p className="science-key-insight">
              There are no "better" poles. Someone high in Freedom isn't more evolved than someone high in Stability—they're simply optimized for different environments and life designs. Your results show where you naturally thrive, not what you should become.
            </p>
          </div>
        </div>

        {/* Growth Edge Section */}
        <div className="growth-edge-section">
          <h3 className="section-title-minimal">YOUR GROWTH EDGE</h3>
          <p className="growth-edge-text-minimal">{archetype.growthEdge}</p>
        </div>

        {/* Axis Legend - Only show on desktop since mobile has the list above */}
        {!isMobile && (
          <div className="results-section-minimal">
            <h3 className="section-title-minimal">HOW TO READ THIS</h3>
            <div className="legend-grid">
              {BECOMING_AXES.map((axis, i) => {
                const [left, right] = axis.split('↔');
                const v = normalizedValues[i];
                const leaning = v > 0.55 ? right.trim() : v < 0.45 ? left.trim() : 'Balanced';
                const pct = Math.round(v * 100);
                return (
                  <div key={axis} className="legend-row">
                    <span className="legend-axis">{left.trim()} vs. {right.trim()}</span>
                    <div className="legend-meter">
                      <div className="legend-fill" style={{ width: `${pct}%`, backgroundColor: archetype.colorHex }} />
                    </div>
                    <span className="legend-value">{leaning} · {pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dominant Poles Section */}
        <div className="results-section-minimal">
          <h3 className="section-title-minimal">YOUR DOMINANT TENDENCIES</h3>
          <div className="dominant-poles-list">
            {dominantPoles.map((item, index) => (
              <div key={index} className="pole-item">
                <div className="pole-row">
                  <span className="pole-label">{item.dimension.replace('↔', ' vs. ')}</span>
                  <span className="pole-value">{item.pole}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Growth Suggestions */}
        <div className="results-section-minimal">
          <h3 className="section-title-minimal">PATHS TO HARMONY</h3>
          <p className="section-description-minimal">
            These are your edges for integration—dimensions where exploring the opposite pole could bring more wholeness and flexibility.
          </p>
          <div className="suggestions-list">
            {growthSuggestions.map((item, index) => {
              const [left, right] = item.dimension.split('↔');
              return (
                <div key={index} className="suggestion-item">
                  <div className="suggestion-header">
                    <div className="suggestion-dimension-minimal">
                      {item.dimension.replace('↔', ' vs. ')}
                    </div>
                    <div className="suggestion-badge">Growth Opportunity</div>
                  </div>
                  <p className="suggestion-text-minimal">{item.suggestion}</p>
                  <div className="suggestion-why">
                    <strong>Why this matters:</strong> {DIMENSION_WHY[item.dimension]}
                  </div>
                  <div className="suggestion-practice">
                    <strong>Practice:</strong> {getPracticeForDimension(item.dimension, item.type)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="results-cta">
          <h3 className="cta-title">Continue Your Journey</h3>
          <p className="cta-description">
            Manifest your best self with friends on Geists. Track your growth, 
            connect authentically, and become who you're meant to be.
          </p>
          <div className="cta-buttons">
            <button className="cta-button primary" onClick={handleDownloadApp}>
              Download Geists
            </button>
            <button className="cta-button secondary" onClick={handleRetake}>
              Retake Quiz
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="results-footer">
          <p>Share your archetype with friends and discover theirs</p>
          <div className="share-buttons">
            <button 
              className="share-button"
              onClick={async () => {
                const text = `I just discovered I'm ${archetype.name}! "${archetype.mantra}" Find out your archetype at geists.app`;
                if (navigator.share) {
                  try {
                    await navigator.share({ text });
                  } catch (err) {
                    // User canceled share - ignore the error
                    if (err.name !== 'AbortError') {
                      console.error('Error sharing:', err);
                    }
                  }
                } else {
                  try {
                    await navigator.clipboard.writeText(text);
                    alert('Copied to clipboard!');
                  } catch (err) {
                    console.error('Error copying to clipboard:', err);
                  }
                }
              }}
            >
              Share Results
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizResults;

